import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  ActionDropdownProps,
  ActionItemOption,
  CmhcIcon,
  CmhcIconSize
} from '@cmhc-schl/hearth-ui';
import {
  ClickAwayListener,
  Paper,
  Popper,
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton
} from '@mui/material';
import styles from './styles.module.scss';

interface ActionDropdownWithListItemsOption extends ActionItemOption {
  chipLabel?: string;
}

interface ActionDropdownWithListItemsProps extends Omit<ActionDropdownProps, 'options'> {
  options: ActionDropdownWithListItemsOption[];
}

export const ActionDropdownWithListItems: React.FC<ActionDropdownWithListItemsProps> = memo(
  ({
    anchorEl,
    open,
    popperModifiers = [
      {
        name: 'offset',
        options: {
          offset: [0, 0]
        }
      }
    ],
    onToggle,
    options,
    trigger,
    className
  }) => {
    const anchorRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const [isMounted, setIsMounted] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [showScroll, setShowScroll] = useState(false);

    if (!options || options.length === 0) return null;

    const handleClose = useCallback(() => {
      setFocusedIndex(0);
      onToggle(false);
    }, [onToggle]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLElement>) => {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setFocusedIndex((prev) => (prev + 1) % options.length);
            break;
          case 'ArrowUp':
            e.preventDefault();
            setFocusedIndex((prev) => (prev - 1 + options.length) % options.length);
            break;
          case 'Enter':
          case ' ':
            e.preventDefault();
            onToggle(!open);
            break;
          case 'Escape':
            e.preventDefault();
            handleClose();
            break;
          default:
            e.preventDefault();
            setFocusedIndex(options.length - 1);
            break;
        }
      },
      [focusedIndex, options, handleClose]
    );

    const handleItemClick = useCallback(
      (option: ActionDropdownWithListItemsOption, e: React.MouseEvent<HTMLElement>) => {
        option.onClick?.(e);

        if (anchorEl instanceof HTMLElement) {
          setTimeout(() => anchorEl.focus(), 0);
        }
      },
      [anchorEl]
    );

    useEffect(() => {
      if (open && menuRef.current) {
        const firstItem = menuRef.current.querySelector('li') as HTMLLIElement;
        if (firstItem) {
          firstItem.focus();
        }
      }
    }, [open]);

    useEffect(() => {
      if (anchorRef.current) {
        setIsMounted(true);
      }
    }, []);

    useEffect(() => {
      const checkHeight = () => {
        if (anchorEl) {
          setShowScroll(anchorEl?.offsetHeight > 240);
        }
      };
      checkHeight();
      window.addEventListener('resize', checkHeight);
      return () => window.removeEventListener('resize', checkHeight);
    }, []);

    const handleToggle = (event: React.MouseEvent | React.KeyboardEvent) => {
      if (event.type === 'click') {
        onToggle(!open);
      }
    };

    return (
      <ClickAwayListener onClickAway={handleClose}>
        <div>
          <Box
            onClick={handleToggle}
            ref={anchorRef}
            aria-haspopup="true"
            onKeyDown={handleKeyDown}
          >
            {trigger}
          </Box>
          {isMounted && (
            <Popper
              open={open}
              anchorEl={anchorEl ?? anchorRef.current}
              placement="bottom-start"
              className={`${styles.actionDropdownListItemsBody} ${className} ${
                showScroll ? styles.showScorll : ''
              }`}
              modifiers={popperModifiers}
              data-testid="action-dropdown-list"
              disablePortal
            >
              <Paper className={styles.actionMenuListItemsPaper}>
                <List dense ref={menuRef} sx={{ padding: 0 }}>
                  {options.map((option, index) => (
                    <ListItem key={option.value} tabIndex={0} className={styles.actionMenuListItem}>
                      <ListItemText primary={option.chipLabel} sx={{ margin: 0 }} />
                      {option.icon && (
                        <IconButton
                          edge="end"
                          aria-label={`remove tab ${option.chipLabel}`}
                          onClick={(e: React.MouseEvent<HTMLElement>) => handleItemClick(option, e)}
                          onFocus={() => setFocusedIndex(index)}
                          sx={{
                            padding: '12px',
                            color: '#1F2428'
                          }}
                        >
                          <CmhcIcon type={'icon_close'} size={CmhcIconSize.SMALL} />
                        </IconButton>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Popper>
          )}
        </div>
      </ClickAwayListener>
    );
  }
);
